// src/components/layouts/ClipVideoSequences.tsx
import React from 'react';
import { Sequence } from 'remotion';
import { ClipPlaybackVideo } from '@/components/preview/ClipPlaybackVideo';
import { EditorPlaybackStack } from '@/components/preview/EditorPlaybackStack';
import { useSourceAspectRatio } from '@/remotion/useSourceAspectRatio';
import { COVER_CROP_CONTAINER_STYLE, computeCoverCropWrapperStyle } from '@/lib/crop/computeCoverCropWrapperStyle';
import { buildSceneLayout, type CutWindow } from '@/lib/transitions/buildSceneLayout';
import type { SceneTransition } from '@/lib/transitions/types';
import { sceneAudioFades, makeSceneVolume } from '@/lib/transitions/resolveSceneVolume';
import { SceneTransitionWrapper, TransitionDipOverlays } from '@/components/remotion/SceneTransitions';

export interface ClipInterval {
  startTime: number;
  endTime: number;
}

/**
 * Frame-accurate sequence shape. `from` is the OUTPUT-timeline frame where the
 * sequence is placed; `startFrom`/`endAt` are SOURCE frames to trim from the
 * full source video. Mirrors the SplitSpeakerComposition sequence construction
 * ({ from: finalStartFrame, startFrom: startFrame, endAt: endFrame }).
 */
export interface FrameSequence {
  from: number;
  startFrom: number;
  endAt: number;
}

/**
 * Renders the clip's selected intervals as back-to-back video sequences,
 * filling its parent (the parent decides the region / aspect). Used as the
 * `video` slot for post-frame layouts, in both the editor preview and the
 * Remotion PostFrame composition.
 *
 * Uses the frame-accurate <Video> from @remotion/media. The legacy `Video`
 * from 'remotion' seeks an HTML5 video element per rendered frame, which is
 * NOT frame accurate — post-layout exports showed frames attributed up to ±4
 * frames off, oscillating back and forth. Because the @remotion/media canvas
 * ignores CSS object-fit/object-position, the fit is passed as a PROP and
 * reframe crops use an oversized wrapper div (computeCoverCropWrapperStyle).
 *
 * Two mutually-exclusive input modes:
 * - `frameSequences` (frame-accurate): used by the Remotion export path. Each
 *   item already carries the output placement (`from`) and the SOURCE trim
 *   (`startFrom`/`endAt`) in frames. Rendered directly.
 * - `intervals` (source seconds): used by the editor preview. `startTime`/
 *   `endTime` are SOURCE seconds; sequences are laid back-to-back on the output
 *   timeline. This is the original behaviour and is left unchanged.
 */
export const ClipVideoSequences: React.FC<{
  videoUrl: string;
  intervals?: ClipInterval[];
  frameSequences?: FrameSequence[];
  fps: number;
  isMuted?: boolean;
  volume?: number;
  /** How the clip fills its region. Post layouts with cropped halves use 'cover'. */
  objectFit?: 'contain' | 'cover';
  /** "Reenquadrar vídeo" crop keyframe for the CURRENT frame ({x,y} in
   *  percentages, zoom ≥ 1). Applied only for 'cover' fits: the wrapper div
   *  picks which part of the source shows; zoom scales around that point.
   *  Hosts resolve it per frame (resolveCropPosition) so layout cells honor
   *  the same reframe keyframes as the default composition. */
  cropPosition?: { x: number; y: number; zoom: number } | null;
  /** Per-cut scene transitions. In `intervals` mode pass `transitions` (the
   *  layout is computed here); in `frameSequences` mode pass `cuts` derived
   *  by the host via cutsFromClipIntervals (the backend already placed the
   *  sequences). */
  transitions?: SceneTransition[];
  cuts?: CutWindow[];
  /** Browser Player: HTML5 Video + `#t` fragment. Export: `@remotion/media`
   *  canvas decoder (frame-accurate, not suitable for long library seeks). */
  playback?: 'html5' | 'media';
  /** Browser-preview media failures. Export hosts omit this and keep Remotion's
   * normal fallback behavior. */
  onMediaError?: (error: Error) => void;
  /** Editor Player clock. Export renders omit this. */
  previewPlaying?: boolean;
  onPreviewBuffering?: (buffering: boolean) => void;
  onPreviewMediaReady?: () => void;
}> = ({ videoUrl, intervals = [], frameSequences, fps, isMuted = false, volume = 1, objectFit = 'contain', cropPosition = null, transitions = [], cuts, playback = 'media', onMediaError, previewPlaying = false, onPreviewBuffering, onPreviewMediaReady }) => {
  const sourceAspect = useSourceAspectRatio(videoUrl);

  const coverCrop = objectFit === 'cover' && cropPosition;
  const wrapperStyle = coverCrop
    ? computeCoverCropWrapperStyle({ cropPosition, sourceAspect })
    : null;

  // With a crop wrapper, the wrapper owns the cover geometry (its aspect ratio
  // equals the source's) and the video just fills it.
  const renderVideo = (
    trimBefore: number,
    trimAfter: number,
    volumeFn?: (f: number) => number,
  ) => {
    const video = (
      <ClipPlaybackVideo
        videoUrl={videoUrl}
        trimBefore={trimBefore}
        trimAfter={trimAfter}
        fps={fps}
        volume={volumeFn ?? (isMuted ? 0 : volume)}
        objectFit={wrapperStyle ? 'fill' : objectFit}
        isMuted={isMuted || volume === 0}
        playback={playback}
        onMediaError={onMediaError}
      />
    );
    return wrapperStyle ? (
      <div style={COVER_CROP_CONTAINER_STYLE}>
        <div data-testid="clip-video-cover-crop" style={wrapperStyle}>
          {video}
        </div>
      </div>
    ) : (
      video
    );
  };

  if (playback === 'html5' && !frameSequences) {
    const layout = buildSceneLayout({ scenes: intervals, transitions, fps });
    const fit = wrapperStyle ? 'fill' : objectFit;
    const wrap = (video: React.ReactNode) => wrapperStyle ? (
      <div style={COVER_CROP_CONTAINER_STYLE}>
        <div data-testid="clip-video-cover-crop" style={wrapperStyle}>{video}</div>
      </div>
    ) : video;
    return (
      <>
        <EditorPlaybackStack
          videoUrl={videoUrl}
          blocks={layout.blocks}
          cuts={layout.cuts}
          fps={fps}
          baseVolume={isMuted ? 0 : volume}
          muted={isMuted || volume === 0}
          playing={previewPlaying}
          objectFit={fit}
          onBufferingChange={onPreviewBuffering}
          onReady={onPreviewMediaReady}
          onMediaError={onMediaError}
          renderPrimary={wrap}
          renderIncoming={wrap}
        />
        <TransitionDipOverlays cuts={layout.cuts} />
      </>
    );
  }

  if (frameSequences) {
    const activeCuts = cuts ?? [];
    return (
      <>
        {frameSequences.map((seq, index) => {
          const dur = Math.max(0, seq.endAt - seq.startFrom);
          if (dur <= 0) return null;
          const fades = sceneAudioFades(activeCuts, index);
          const vol = makeSceneVolume({
            baseVolume: isMuted ? 0 : volume,
            fadeInFrames: fades.fadeInFrames,
            fadeOutFrames: fades.fadeOutFrames,
            durationInFrames: dur,
          });
          return (
            <Sequence key={index} from={seq.from} durationInFrames={dur} premountFor={150}>
              <SceneTransitionWrapper cuts={activeCuts} sceneIndex={index} blockFrom={seq.from}>
                {renderVideo(seq.startFrom, seq.endAt, vol)}
              </SceneTransitionWrapper>
            </Sequence>
          );
        })}
        <TransitionDipOverlays cuts={activeCuts} />
      </>
    );
  }

  const layout = buildSceneLayout({ scenes: intervals, transitions, fps });
  return (
    <>
      {layout.blocks.map((block, index) => {
        if (block.durationInFrames <= 0) return null;
        const fades = sceneAudioFades(layout.cuts, index);
        const vol = makeSceneVolume({
          baseVolume: isMuted ? 0 : volume,
          fadeInFrames: fades.fadeInFrames,
          fadeOutFrames: fades.fadeOutFrames,
          durationInFrames: block.durationInFrames,
        });
        return (
          <Sequence key={index} from={block.from} durationInFrames={block.durationInFrames} premountFor={150}>
            <SceneTransitionWrapper cuts={layout.cuts} sceneIndex={index} blockFrom={block.from}>
              {renderVideo(block.trimBefore, block.trimAfter, vol)}
            </SceneTransitionWrapper>
          </Sequence>
        );
      })}
      <TransitionDipOverlays cuts={layout.cuts} />
    </>
  );
};
